import config from "../../../config";
import api from "../api/apiClient";
import { StorageService } from "./storageService";
import { UserResponse } from "../../models/core/user";

// ── helpers ──────────────────────────────────────────────────────────────────

const isPrototype = () => config.appMode === "prototype";

/** Unwrap Earlyscape envelope: { success, data: { ... } } */
function unwrap<T>(raw: { data: { data: T } }): T {
  return raw.data.data;
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface RegisterStudentInput {
  name: string;
  email: string;
  password: string;
  schoolCode: string;
  branchId?: string;
}

export interface RegisterAdminInput {
  name: string;
  email: string;
  password: string;
  schoolName: string;
  schoolCode: string;
  principalName: string;
  principalEmail: string;
  principalPhone: string;
  phone: string;
  address: { street?: string; city: string; state: string; country: string; coordinates?: { latitude: number; longitude: number } };
}

export interface LoginInput {
  email: string;
  password: string;
  userType?: "student" | "school_admin";
}

// ── AuthService ───────────────────────────────────────────────────────────────

export class AuthService {
  // ── Registration ────────────────────────────────────────────────────────────

  static async registerStudent(input: RegisterStudentInput): Promise<{
    user: UserResponse;
    accessToken: string;
    refreshToken: string;
  }> {
    if (isPrototype()) {
      const user = await StorageService.create("users", {
        name: input.name,
        email: input.email,
        password: input.password,
        userType: "student",
        role: "student",
      });
      await StorageService.create("students", {
        firstName: input.name.split(" ")[0],
        lastName: input.name.split(" ").slice(1).join(" ") || input.name,
        email: input.email,
        userId: user._id,
        metadata: { onboardingCompleted: false },
      });
      return { user: user as UserResponse, accessToken: "prototype", refreshToken: "prototype" };
    }

    const raw = await api.post("/auth/register", input);
    const data = unwrap<{ user: UserResponse; accessToken: string; refreshToken: string }>(raw);
    return data;
  }

  static async registerAdmin(input: RegisterAdminInput): Promise<{
    user: UserResponse;
    accessToken: string;
    refreshToken: string;
  }> {
    if (isPrototype()) {
      const user = await StorageService.create("users", {
        name: input.name,
        email: input.email,
        password: input.password,
        userType: "school_admin",
        role: "school",
      });
      return { user: user as UserResponse, accessToken: "prototype", refreshToken: "prototype" };
    }

    const raw = await api.post("/auth/register/admin", input);
    const data = unwrap<{ user: UserResponse; accessToken: string; refreshToken: string }>(raw);
    return data;
  }

  // ── Login ────────────────────────────────────────────────────────────────────

  static async login(credentials: LoginInput): Promise<{
    user: UserResponse;
    accessToken: string;
    refreshToken: string;
  }> {
    if (isPrototype()) {
      const users = await StorageService.search("users", {
        email: credentials.email,
        password: credentials.password,
      });
      const user = users[0];
      if (!user) throw new Error("Invalid email or password");
      StorageService.write("currentUser", user);

      // Attach routing flags from localStorage student record
      const students = StorageService.read("students") as any[];
      const student = students.find((s: any) => s.userId === user._id || s.email === user.email);
      const enriched: UserResponse = {
        ...(user as UserResponse),
        onboardingCompleted: student?.metadata?.onboardingCompleted === true,
        hasTraitProfile: student?.traitProfile != null,
      };

      return { user: enriched, accessToken: "prototype", refreshToken: "prototype" };
    }

    const endpoint = credentials.userType === "school_admin" ? "/auth/admin/login" : "/auth/login";

    const raw = await api.post(endpoint, credentials);
    const data = unwrap<{
      user: UserResponse & { onboardingCompleted?: boolean; hasTraitProfile?: boolean };
      accessToken: string;
      refreshToken: string;
    }>(raw);
    return data;
  }

  // ── Logout ───────────────────────────────────────────────────────────────────

  static async logout(): Promise<void> {
    if (!isPrototype()) {
      try {
        await api.post("/auth/logout");
      } catch {
        // ignore — client will drop tokens regardless
      }
    }
    StorageService.delete("currentUser");
  }

  // ── Current user ─────────────────────────────────────────────────────────────

  static getCurrentUser(): UserResponse | null {
    const raw = StorageService.find("currentUser");
    return raw ? JSON.parse(raw) as UserResponse : null;
  }

  static isAuthenticated(): boolean {
    return !!StorageService.find("currentUser");
  }

  // ── Password reset ────────────────────────────────────────────────────────────

  static async requestPasswordReset(email: string): Promise<void> {
    if (isPrototype()) {
      console.log(`[prototype] Password reset requested for ${email}`);
      return;
    }
    await api.post("/auth/forgot-password", { email });
  }

  static async resetPassword(token: string, newPassword: string): Promise<void> {
    if (isPrototype()) {
      const users = StorageService.read("users") as any[];
      const user = users.find((u: any) =>
        u.passwordResetToken === token &&
        u.passwordResetExpires &&
        new Date() < new Date(u.passwordResetExpires),
      );
      if (!user) throw new Error("Invalid or expired reset token");
      await StorageService.update("users", user._id, {
        password: newPassword,
        passwordResetToken: undefined,
        passwordResetExpires: undefined,
      });
      return;
    }
    await api.post(`/auth/reset-password/${token}`, { password: newPassword });
  }

  // ── Profile ───────────────────────────────────────────────────────────────────

  static async updateProfile(userId: string, updates: Partial<UserResponse>): Promise<UserResponse> {
    if (isPrototype()) {
      const updated = await StorageService.update("users", userId, updates);
      StorageService.write("currentUser", updated);
      return updated as UserResponse;
    }
    const raw = await api.patch("/users/me", updates);
    return unwrap<UserResponse>(raw);
  }
}
